/**
 * Custom hook for subscribing to posting queues for multiple accounts
 *
 * Uses Firestore real-time listeners for queue metadata and comments.
 * VM status (from GCP API) is polled every 30s only when any queue is running.
 * Falls back to REST API polling if Firestore fails.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AccountInfo, PendingComment, PostingQueueStatus, api } from '../services/api';
import {
    subscribeToPostingQueueMeta,
    subscribeToPostingQueueComments,
    PostingQueueMeta,
} from '../services/firestoreListeners';
import { isFirebaseInitialized } from '../config/firebase';
import { encodeAccountId } from '../services/common';

// Extended queue item with account info for display
interface QueueItemWithAccount extends PendingComment {
    accountUrl: string;
    accountId: string;
}

interface UsePostingQueuesSubscriptionResult {
    /** All pending queue items across all accounts */
    allQueueItems: QueueItemWithAccount[];
    /** VM statuses keyed by accountId */
    vmStatuses: { [accountId: string]: PostingQueueStatus };
    /** Whether using real-time Firestore listeners */
    isUsingRealtime: boolean;
    /** Whether currently loading */
    loading: boolean;
    /** Error message if any */
    error: string | null;
    /** Manually refresh all data */
    refresh: () => void;
}

interface UsePostingQueuesSubscriptionOptions {
    /** Enable polling even when Firestore is working (for debugging) */
    forcePolling?: boolean;
    /** VM status polling interval in milliseconds (default: 30000) */
    vmPollInterval?: number;
    /** Full polling interval in milliseconds when Firestore fails (default: 10000) */
    fullPollInterval?: number;
    /** Limit of comments per account to subscribe to (default: 100) */
    commentsLimit?: number;
}

/**
 * Hook to subscribe to posting queue updates for multiple accounts
 * Falls back to REST API polling if Firestore fails
 *
 * @param accounts - List of accounts to subscribe to
 * @param options - Configuration options
 */
export function usePostingQueuesSubscription(
    accounts: AccountInfo[],
    options: UsePostingQueuesSubscriptionOptions = {}
): UsePostingQueuesSubscriptionResult {
    const {
        forcePolling = false,
        vmPollInterval = 30000,  // 30 seconds
        fullPollInterval = 10000,  // 10 seconds for fallback
        commentsLimit = 100,
    } = options;

    // State
    const [queueMetaByAccount, setQueueMetaByAccount] = useState<{ [accountId: string]: PostingQueueMeta }>({});
    const [commentsByAccount, setCommentsByAccount] = useState<{ [accountId: string]: PendingComment[] }>({});
    const [vmStatuses, setVmStatuses] = useState<{ [accountId: string]: PostingQueueStatus }>({});
    const [isUsingRealtime, setIsUsingRealtime] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Refs for cleanup
    const metaUnsubsRef = useRef<{ [accountId: string]: () => void }>({});
    const commentsUnsubsRef = useRef<{ [accountId: string]: () => void }>({});
    const vmPollTimerRef = useRef<NodeJS.Timeout | null>(null);
    const fullPollTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Compute all queue items from state
    const allQueueItems = useMemo(() => {
        const items: QueueItemWithAccount[] = [];
        for (const account of accounts) {
            const accountId = encodeAccountId(account.accountUrl);
            const comments = commentsByAccount[accountId] || [];
            for (const comment of comments) {
                items.push({
                    ...comment,
                    accountUrl: account.accountUrl,
                    accountId,
                });
            }
        }
        return items;
    }, [accounts, commentsByAccount]);

    // Check if any queue is running (for VM polling)
    const anyQueueRunning = useMemo(() => {
        return Object.values(queueMetaByAccount).some(meta => meta.isPostingQueueRunning);
    }, [queueMetaByAccount]);

    // REST API fallback: fetch all queues
    const fetchViaRestApi = useCallback(async (sync: boolean = false) => {
        if (accounts.length === 0) {
            setLoading(false);
            return;
        }

        try {
            const results = await Promise.all(
                accounts.map(async (account) => {
                    const accountId = encodeAccountId(account.accountUrl);
                    try {
                        const [status, preview] = await Promise.all([
                            api.getPostingQueueStatus(accountId, sync),
                            api.getPostingQueuePreview(accountId, commentsLimit),
                        ]);
                        return { accountId, accountUrl: account.accountUrl, status, preview };
                    } catch {
                        return null;
                    }
                })
            );

            const newVmStatuses: { [key: string]: PostingQueueStatus } = {};
            const newComments: { [key: string]: PendingComment[] } = {};

            for (const result of results) {
                if (result) {
                    newVmStatuses[result.accountId] = result.status;
                    newComments[result.accountId] = result.preview.comments;
                }
            }

            setVmStatuses(newVmStatuses);
            setCommentsByAccount(newComments);
            setError(null);
            setLoading(false);
        } catch (err) {
            console.error('[usePostingQueuesSubscription] REST API fetch error:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch queue data');
            setLoading(false);
        }
    }, [accounts, commentsLimit]);

    // Poll VM status for accounts with running queues
    const pollVmStatuses = useCallback(async () => {
        if (accounts.length === 0) return;

        const runningAccountIds = Object.entries(queueMetaByAccount)
            .filter(([_, meta]) => meta.isPostingQueueRunning)
            .map(([accountId]) => accountId);

        if (runningAccountIds.length === 0) return;

        console.log('[usePostingQueuesSubscription] Polling VM status for running queues:', runningAccountIds);

        try {
            const results = await Promise.all(
                runningAccountIds.map(async (accountId) => {
                    try {
                        const status = await api.getPostingQueueStatus(accountId, false);
                        return { accountId, status };
                    } catch {
                        return null;
                    }
                })
            );

            setVmStatuses(prev => {
                const updated = { ...prev };
                for (const result of results) {
                    if (result) {
                        updated[result.accountId] = result.status;
                    }
                }
                return updated;
            });
        } catch (err) {
            console.error('[usePostingQueuesSubscription] VM status poll error:', err);
        }
    }, [accounts, queueMetaByAccount]);

    // Start/stop VM polling based on queue running state
    useEffect(() => {
        if (!isUsingRealtime) return;

        if (anyQueueRunning) {
            console.log('[usePostingQueuesSubscription] Starting VM status polling');
            pollVmStatuses();  // Initial fetch
            vmPollTimerRef.current = setInterval(pollVmStatuses, vmPollInterval);
        } else {
            if (vmPollTimerRef.current) {
                console.log('[usePostingQueuesSubscription] Stopping VM status polling');
                clearInterval(vmPollTimerRef.current);
                vmPollTimerRef.current = null;
            }
        }

        return () => {
            if (vmPollTimerRef.current) {
                clearInterval(vmPollTimerRef.current);
                vmPollTimerRef.current = null;
            }
        };
    }, [anyQueueRunning, isUsingRealtime, pollVmStatuses, vmPollInterval]);

    // Stop full polling
    const stopFullPolling = useCallback(() => {
        if (fullPollTimerRef.current) {
            clearInterval(fullPollTimerRef.current);
            fullPollTimerRef.current = null;
        }
    }, []);

    // Start full polling (fallback when Firestore fails)
    const startFullPolling = useCallback(() => {
        stopFullPolling();

        console.log('[usePostingQueuesSubscription] Starting full REST API polling');
        fetchViaRestApi(true);  // Initial fetch with sync
        fullPollTimerRef.current = setInterval(() => fetchViaRestApi(false), fullPollInterval);
    }, [fetchViaRestApi, fullPollInterval, stopFullPolling]);

    // Setup Firestore listeners for all accounts
    const setupFirestoreListeners = useCallback(() => {
        if (accounts.length === 0) {
            setLoading(false);
            return;
        }

        console.log('[usePostingQueuesSubscription] Setting up Firestore listeners for', accounts.length, 'accounts');
        let hasSetRealtime = false;

        /* eslint-disable no-loop-func */
        for (const account of accounts) {
            const accountId = encodeAccountId(account.accountUrl);

            // Subscribe to queue metadata
            metaUnsubsRef.current[accountId] = subscribeToPostingQueueMeta(
                accountId,
                (meta) => {
                    if (!hasSetRealtime) {
                        setIsUsingRealtime(true);
                        setLoading(false);
                        hasSetRealtime = true;
                    }

                    if (meta) {
                        setQueueMetaByAccount(prev => ({ ...prev, [accountId]: meta }));

                        // Convert meta to PostingQueueStatus for vmStatuses
                        setVmStatuses(prev => ({
                            ...prev,
                            [accountId]: {
                                accountId,
                                status: meta.status,
                                pendingCount: prev[accountId]?.pendingCount ?? 0,
                                vncUrl: meta.vncUrl,
                                vmInstanceName: meta.vmInstanceName,
                                isPostingQueueRunning: meta.isPostingQueueRunning,
                                needsLoginSince: meta.needsLoginSince,
                            },
                        }));
                    } else {
                        // No meta - account may not have queue initialized
                        setQueueMetaByAccount(prev => {
                            const updated = { ...prev };
                            delete updated[accountId];
                            return updated;
                        });
                    }
                },
                (err) => {
                    console.error('[usePostingQueuesSubscription] Queue meta listener error for', accountId, ':', err);
                    // Fall back to polling if Firestore fails
                    if (!hasSetRealtime) {
                        setIsUsingRealtime(false);
                        setError('Firestore connection failed, using polling');
                        startFullPolling();
                    }
                }
            );

            // Subscribe to pending comments
            commentsUnsubsRef.current[accountId] = subscribeToPostingQueueComments(
                accountId,
                (comments) => {
                    setCommentsByAccount(prev => ({ ...prev, [accountId]: comments }));

                    // Update pending count in vmStatuses
                    setVmStatuses(prev => {
                        const existing = prev[accountId];
                        if (existing) {
                            return {
                                ...prev,
                                [accountId]: { ...existing, pendingCount: comments.length },
                            };
                        }
                        return prev;
                    });
                },
                (err) => {
                    console.error('[usePostingQueuesSubscription] Comments listener error for', accountId, ':', err);
                },
                commentsLimit
            );
        }
        /* eslint-enable no-loop-func */

        // Set loading to false after a short delay if no data received yet
        setTimeout(() => {
            setLoading(false);
        }, 2000);
    }, [accounts, commentsLimit, startFullPolling]);

    // Cleanup function
    const cleanup = useCallback(() => {
        // Stop polling
        if (vmPollTimerRef.current) {
            clearInterval(vmPollTimerRef.current);
            vmPollTimerRef.current = null;
        }
        stopFullPolling();

        // Unsubscribe from all listeners
        for (const unsub of Object.values(metaUnsubsRef.current)) {
            unsub();
        }
        metaUnsubsRef.current = {};

        for (const unsub of Object.values(commentsUnsubsRef.current)) {
            unsub();
        }
        commentsUnsubsRef.current = {};
    }, [stopFullPolling]);

    // Manual refresh
    const refresh = useCallback(() => {
        if (isUsingRealtime) {
            pollVmStatuses();
        } else {
            fetchViaRestApi(true);
        }
    }, [isUsingRealtime, pollVmStatuses, fetchViaRestApi]);

    // Main effect: setup/teardown based on accounts
    useEffect(() => {
        // Reset state
        setLoading(true);
        setQueueMetaByAccount({});
        setCommentsByAccount({});
        setVmStatuses({});
        setError(null);
        setIsUsingRealtime(false);

        if (accounts.length === 0) {
            setLoading(false);
            return;
        }

        // Cleanup previous listeners
        cleanup();

        // Check if we should use Firestore or polling
        if (forcePolling || !isFirebaseInitialized()) {
            console.log('[usePostingQueuesSubscription] Using REST API polling');
            startFullPolling();
        } else {
            console.log('[usePostingQueuesSubscription] Setting up Firestore real-time listeners');
            setupFirestoreListeners();
        }

        return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accounts.length, forcePolling]); // Only re-setup when account count changes or forcePolling changes

    // Re-setup listeners when accounts change (but count stays same)
    const accountIdsRef = useRef<string[]>([]);
    useEffect(() => {
        const currentIds = accounts.map(a => encodeAccountId(a.accountUrl)).sort();
        const prevIds = accountIdsRef.current;

        if (JSON.stringify(currentIds) !== JSON.stringify(prevIds)) {
            accountIdsRef.current = currentIds;

            // Only re-setup if we're using real-time
            if (isUsingRealtime && isFirebaseInitialized() && !forcePolling) {
                cleanup();
                setupFirestoreListeners();
            }
        }
    }, [accounts, isUsingRealtime, forcePolling, cleanup, setupFirestoreListeners]);

    return {
        allQueueItems,
        vmStatuses,
        isUsingRealtime,
        loading,
        error,
        refresh,
    };
}
